import { Dictionary } from "../store/models/common_types";
import * as Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more.src";
import Tree from "highcharts/modules/treemap";

HighchartsMore(Highcharts);
Tree(Highcharts);
export interface TickerInfo {
  company_id: number;
  full_name: string;
}
interface ApiResponse {
  status: string;
  message: string;
  data: any;
}

type PharmaApiResponse = Omit<ApiResponse, "data">;
export const NoDataMsg = "No data to display";

export interface DrugsByCompanyApiResponse extends PharmaApiResponse {
  data: {
    count: number;
    total: number;
    selected_ticker_rank: number;
    tickers: Dictionary<TickerInfo>;
    values: Array<any>;
  };
}
export interface DrugsByMoaApiResponse extends PharmaApiResponse {
  data: {
    count: number;
    total: number;
    selected_moa_rank: number;
    values: Array<any>;
  };
}

export interface DrugsByPhaseApiResponse extends PharmaApiResponse {
  data: {
    values: Array<any>;
  };
}

export interface DrugsByIndicationApiResponse extends PharmaApiResponse {
  data: {
    count: number;
    total: number;
    values: Array<any>;
    selected_indication_rank: number;
  };
}

export interface DrugsByTherapeuticAreaApiResponse extends PharmaApiResponse {
  data: {
    values: Array<any>;
  };
}

export const ChartStateColors = {
  deselected: "#D8EDFA",
  selected: "#1874AE",
  hover: "#8AC8EF",
  disabled: "#E9EBEE",
  selectedFontColor: "#FFFFFF",
  disabledFontColor: "#909AA9",
  deselectedFontColor: "#16181C",
  hoverFontColor: "#16181C",
  historicPeriod: "#0C3B58",
  peakSales: "#1874AE",
  futurePeriod: "#63B6EA",
  pieColors: [
    "#02080D",
    "#0C3B58",
    "#0F4B71",
    "#166DA3",
    "#1874AE",
    "#1B82C3",
    "#1D8FD6",
    "#289AE2",
    "#4FADE7",
    "#63B6EA",
    "#8AC8EF",
    "#9DD1F2",
    "#B1DAF4",
    "#C4E4F7",
    "#D8EDFA",
    "#EBF6FC",
  ],
};

export const GlobalHighchartOptions: Highcharts.Options = {
  time: {
    useUTC: false,
  },
  lang: {
    thousandsSep: ",",
    loading: "Crunching Numbers...",
    noData: NoDataMsg,
  },
  noData: {
    style: {
      fontWeight: "bold",
      fontSize: "15px",
      color: "#303030",
    },
  },
  loading: {
    labelStyle: {
      color: "black",
      fontWeight: "900",
      opacity: 1,
    },
    style: {
      backgroundColor: "white",
    },
  },
  chart: {
    style: {
      fontFamily:
        "Open sans, Lucida Grande , Segoe UI , Arial, Helvetica, Verdana, sans-serif",
    },
    zoomType: "x",
    panning: {
      enabled: true,
      type: "xy",
    },
    panKey: "shift",
  },
  credits: {
    enabled: false,
  },
  // colors:['#02080D', '#031119', ]
  plotOptions: {
    series: {
      events: {
        legendItemClick: function () {
          const series = this.yAxis.series,
            seriesLen = series.length;
          let visible = this.visible ? 0 : 1;
          // 0 when visible series, because it will be changed after that callback
          for (let i = 0; i < seriesLen; i++) {
            if (series[i].visible) {
              visible++;
            }
          }
          if (visible === 1) {
            return false;
          }
          return true;
        },
      },
      marker: {
        radius: 3,
        states: {
          hover: {
            radius: 3,
            lineColor: "black",
          },
        },
      },
    },
  },
  yAxis: {
    gridLineColor: "#ECF0F1",
    labels: {
      format: "{value:,.2f}",
    },
  },
  title: {
    text: "&nbsp;&nbsp;",
    useHTML: true,
  },
  exporting: {
    showTable: false,
    enabled: false,
    tableCaption: " ",
    scale: 1.2,
    buttons: {
      contextButton: {
        enabled: false,
        menuItems: ["downloadPNG", "downloadPDF"],
      },
    },
    csv: {
      dateFormat: "%d-%b-%Y",
    },
  },
};

export const ColumnChartOptions: Highcharts.Options = {
  chart: {
    type: "bar",
    spacingBottom: 0,
    spacingTop: 0,
    spacingRight: 16,
    spacingLeft: 0,
  },
  title: {
    text: "",
  },
  subtitle: {
    text: "",
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    categories: [],
    title: {
      text: null,
    },
    labels: {
      formatter: function () {
        return `<span class="axis-label" title="${this.value}">${this.value}</span>`;
      },
      useHTML: true,
    },

    gridLineColor: "#FFFFFF",
    lineColor: "#FFFFFF",
  },
  yAxis: {
    min: 0,
    labels: {
      style: {
        width: 60,
      },
      enabled: false,
    },
    title: {
      text: null,
    },
    gridLineColor: "#FFFFFF",
    lineColor: "#FFFFFF",
  },
  exporting: {
    enabled: false,
  },
  plotOptions: {
    series: {
      states: {
        hover: {
          enabled: true,
          color: ChartStateColors.hover,
        },
      },
      cursor: "pointer",
      stickyTracking: false,
      allowPointSelect: false,
      color: ChartStateColors.selected,
      events: {},
      point: {
        events: {
          click: function () {
            this.select(!this.selected, false);
          },
        },
      },
    },

    bar: {
      dataLabels: {
        enabled: true,
        padding: 1,
        align: "left",
        className: "column-data-label",
        crop: false,
        overflow: "allow",
        style: {
          fontWeight: "normal",
          fontSize: "12px",
          fontFamily: "Roboto",
          textOutline: "none",
        },
        useHTML: true,
      },
      pointWidth: 12,
      pointPadding: 0.25,
      states: {
        select: {
          color: ChartStateColors.selected,
        },
        hover: {
          color: ChartStateColors.hover,
        },
      },
    },
  },
  tooltip: { enabled: false },
  credits: {
    enabled: false,
  },
  series: [
    {
      name: "chart",
      type: "bar",
      data: [],
    },
  ],
};

export const ColumnRangeChartOptions: Highcharts.Options = {
  chart: {
    type: "columnrange",
    inverted: true,
    events: {
      load: function () {
        this.series.forEach((series) => {
          series.points.forEach((point) => {
            if (point.color === ChartStateColors.disabled) {
              point.update({
                events: {
                  mouseOver() {
                    return false;
                  },
                  click() {
                    return false;
                  },
                },
                marker: {
                  states: {
                    hover: {
                      enabled: false,
                    },
                  },
                },
              });
            }
          });
        });
      },
    },
  },

  title: {
    text: "",
  },
  subtitle: {
    text: "",
  },
  exporting: {
    enabled: false,
  },
  xAxis: {
    categories: [],
    title: {
      text: null,
    },
    labels: {
      enabled: false,
    },
    gridLineColor: "#FFFFFF",
    lineColor: "#FFFFFF",
  },

  yAxis: {
    labels: {
      enabled: false,
    },
    title: {
      text: null,
    },
    gridLineColor: "#FFFFFF",
    lineColor: "#FFFFFF",
  },

  plotOptions: {
    columnrange: {
      dataLabels: {
        enabled: true,
        inside: true,
        className: "column-data-label",
        formatter: function () {
          const txt =
            this.y === this.point["high"]
              ? this.point.name.split("|")[1]
              : this.x;
          let className = "column-data-label-span";
          if (
            this.point.options.selected ||
            this.point.color === ChartStateColors.selected
          ) {
            className += " selected";
          } else if (this.point.color === ChartStateColors.disabled) {
            className += " disabled";
          } else if (this.point.color === ChartStateColors.deselected) {
            className += " deselected";
          }

          return `<span class="${className}">${txt}</span>`;
        },
        style: {
          fontWeight: "normal",
          color: ChartStateColors.selectedFontColor,
          fontSize: "12px",
          fontFamily: "Roboto",
          textOutline: "none",
        },
        useHTML: true,
      },
      states: {
        select: {
          color: ChartStateColors.selected,
        },
        hover: {
          color: ChartStateColors.hover,
        },
      },
    },
    series: {
      states: {
        hover: {
          enabled: true,
          color: ChartStateColors.hover,
        },
      },
      cursor: "pointer",
      allowPointSelect: false,
      color: ChartStateColors.selected,
      events: {},
      point: {
        events: {
          click: function () {
            this.select(!this.selected, false);
          },
        },
      },
    },
  },
  colors: [ChartStateColors.selected],

  legend: {
    enabled: false,
  },
  tooltip: { enabled: false },
  credits: {
    enabled: false,
  },
  series: [
    {
      name: "chart",
      type: "columnrange",
      data: [],
    },
  ],
};

export const TreeMapChartOptions: Highcharts.Options = {
  title: {
    text: "",
  },
  subtitle: {
    text: "",
  },
  chart: {
    panning: {
      enabled: false,
    },
  },
  exporting: {
    enabled: false,
  },
  plotOptions: {
    series: {
      turboThreshold: 0,
      animation: false,
      states: {
        hover: {
          enabled: true,
          color: ChartStateColors.hover,
        },
      },
      cursor: "pointer",
      allowPointSelect: true,
      color: ChartStateColors.selected,
      events: {},
    },
  },

  legend: {
    enabled: false,
  },
  tooltip: { enabled: true },
  credits: {
    enabled: false,
  },
  navigation: {
    breadcrumbs: {
      formatter: function (breadcrumb: any) {
        let crumb = breadcrumb.levelOptions.name;
        if (breadcrumb.levelOptions.name.length > 13) {
          crumb = `<div><span class="ellipses" >${breadcrumb.levelOptions.name.substring(
            0,
            10,
          )}...</span></div>`;
        }
        return crumb;
      },
      useHTML: true,
      buttonTheme: {
        r: 0,
        states: {
          hover: {
            fill: "none",
          },
        },
      },
    },
  },

  series: [
    {
      name: "All",
      type: "treemap",
      color: "#0A324B",
      layoutAlgorithm: "squarified",
      allowTraversingTree: true,
      levelIsConstant: false,
      borderWidth: 0,
      dataLabels: {
        enabled: false,
      },
      levels: [
        {
          level: 1,
          dataLabels: {
            enabled: true,
            style: {
              fontWeight: "bold",
              fontSize: "12px",
              fontFamily: "Roboto",
              textOutline: "none",
            },
          },

          borderWidth: 1,
          borderColor: "#fff",

          colorVariation: {
            key: "brightness",
            to: 0.8,
          },
        },
        {
          level: 2,
          borderWidth: 0.2,
          borderColor: "#fff",
        },
      ],
      accessibility: {
        exposeAsGroupOnly: true,
      },
      breadcrumbs: {
        floating: false,
        buttonSpacing: 0,
        showFullPath: true,
        useHTML: true,
        style: {
          textOverflow: "ellipsis",
        },
        buttonTheme: {
          style: {
            "text-overflow": "ellipsis",
          },
        },
      },
      point: {
        events: {
          select: function (event: any) {
            if (event.target.node.isLeaf) {
              event.target.color = ChartStateColors.selected;
              this.update(event.target);
            }
          },
          unselect: function (event: any) {
            event.target.color = null;
            this.update(event.target);
          },
        },
      },
      data: [],
    },
  ],
};

export const PieChartOptions: any = {
  chart: {
    type: "pie",
    panning: {
      enabled: false,
    },
  },
  title: {
    text: "",
  },
  subtitle: {
    text: "",
  },
  tooltip: {},
  colors: ChartStateColors.pieColors,
  plotOptions: {
    series: {
      size: "80%",
      allowPointSelect: true,
      cursor: "pointer",
      dataLabels: {
        filter: {
          property: "percentage",
          operator: ">",
          value: 4,
        },
        enabled: true,
        distance: "10%",
        format: "<b>{point.name}</b><br>{point.percentage:.1f} %",
        style: {
          fontWeight: "normal",
          color: "#000",
          fontSize: "12px",
          fontFamily: "Roboto",
          textOutline: "none",
          textOverflow: "clip",
        },
      },
      point: {
        events: {
          click: function () {
            if (this.sliced) {
              this.series.update({
                opacity: 1,
              } as Highcharts.SeriesOptionsType);
            }
          },
        },
      },
    },
  },
  series: [
    {
      name: "chart",
      type: "pie",
      data: [],
    },
  ],
};
const year = new Date().getFullYear();
const copyrightText = `${year} Visible Alpha, L.L.C All Rights Reserved.`;
const exportOption: Highcharts.ExportingOptions = {
  enabled: true,
  allowHTML: true,

  //   buttons: {
  //     contextButton: {
  //         enabled: false
  //     },
  //     exportButton: {
  //         text: '<va-icon name="download"  class="export-chart" size="small" title="Download"></va-icon>',
  //        //text:'<span></span>',
  //         // Use only the download related menu items from the default
  //         // context button
  //         menuItems: [
  //             'downloadPNG',
  //             'downloadPDF'
  //         ],

  //     }
  // },
  chartOptions: {
    title: {
      text: "",
      align: "left",
      x: 10,
    },
    credits: {
      text: copyrightText,
      enabled: true,
      style: { fontSize: "8px" },
    },
    subtitle: {
      text: "",
      align: "left",
      x: 10,
    },
    legend: {
      itemMarginBottom: 10,
    },
  },
  filename: "",
  tableCaption: "",
};

export const BarChartOptions: Highcharts.Options = {
  chart: {
    type: "column",
  },

  xAxis: {
    categories: [],
    title: {
      text: null,
    },
  },
  yAxis: {
    title: {
      text: "Consensus/VA Actuals",
    },
    labels: {
      format: "{value:,0f}",
    },
  },
  exporting: exportOption,

  legend: {
    enabled: false,
  },
  tooltip: {
    pointFormat: " <b>{point.y:.2f}</b>",
  },
  plotOptions: {
    series: {
      events: {},
    },

    column: {
      dataLabels: {
        enabled: true,
        padding: 1,
        align: "center",
        crop: false,
        overflow: "allow",
        style: {
          fontWeight: "600",
          fontSize: "14px",
          fontFamily: "Open Sans",
          color: "#000",
        },
        formatter: function () {
          if (this.key === "Peak Year") {
            return this.key;
          }
          return "";
        },
      },
    },
  },
  series: [
    {
      type: "column",
      data: [],
    },
  ],
};
export const StackChartOptions: Highcharts.Options = {
  chart: {
    type: "column",
  },
  xAxis: {
    categories: [],
  },

  plotOptions: {
    series: {
      stacking: "normal",
    },
  },
  exporting: exportOption,
  series: [
    {
      type: "column",
      name: "A",
      data: [],
    },
    {
      type: "column",
      name: "B",
      data: [],
    },
  ],

  tooltip: {
    pointFormat:
      "{series.name}: <b>{point.y}</b> ({point.percentage:.2f}%)<br/>",
  },
};
export const RangeChartOptions: Highcharts.Options = {
  chart: {
    type: "line",
    marginRight: 360,
  },
  colors: [
    "#058DC7",
    "#50B432",
    "#ED561B",
    "#DDDF00",
    "#24CBE5",
    "#64E572",
    "#FF9655",
    "#FFF263",
    "#6AF9C4",
  ],
  legend: {
    align: "right",
    verticalAlign: "top",
    layout: "vertical",
    itemDistance: 10,
    x: -10,
    y: 0,
    padding: 40,
    labelFormatter: function () {
      const parts = this.name.split(" ");
      const coloredText =
        `<span style="color:black">${parts[0]}</span>` +
        `<span style="color:#667284;font-style: italic;">${parts
          .slice(1)
          .join(" ")}</span>`;
      return coloredText;
    },
  },
  xAxis: {
    categories: [],
    title: {
      text: null,
    },
  },
  yAxis: {
    title: {
      text: "Consensus/VA Actuals",
    },
    labels: {
      format: "{value:,0f}",
    },
  },
  exporting: exportOption,
  tooltip: {
    shared: false,
    pointFormat: " <b>{point.y:.2f}</b>",
  },
  plotOptions: {
    series: {
      marker: {
        enabled: true,
        states: {
          hover: {
            enabled: true,
          },
        },
      },
    },
  },
  series: [],
};
export interface GetGridApiParams {
  offset: number;
  limit: number;
  currency: string;
  period: Array<string>;
  period_type: string;
  period_basis: string;
  percentage_total: boolean;
  viewby: string;
  revision: string;
  revenue_type: string;
  columns: Array<string>;
  sort_key: string;
  sort_order: string;
  filter?: any;
  proxy?: boolean;
}
export interface GetTotalLineApiParams {
  currency: string;
  period: Array<string>;
  period_type: string;
  period_basis: string;
  viewby: string;
  revenue_type: string;
  columns: Array<string>;
  filter?: any;
  proxy?: boolean;
}

export const phaseAbbreviations = {
  Approved: "APP",
  Filed: "FLD",
  "Phase 3": "PH3",
  "Phase 2": "PH2",
  "Phase 1": "PH1",
  Preclinical: "PREC",
  Terminated: "TERM",
};

export const extraInfoKeys = [
  "ticker_data",
  "drug_data",
  "api_data",
  "commercial_status_data",
  "drug_class_data",
  "indication_data",
  "moa_data",
  "phase_data",
  "target_data",
  "therapeutic_area_data",
];

export class FacetFilter {
  company_id: Set<number> = new Set();
  therapeutic_area: Set<number> = new Set();
  indication: Set<number> = new Set();
  phase: Set<number> = new Set();
  moa: Set<number> = new Set();
  drug_class: Set<number> = new Set();
  api: Set<number> = new Set();
  drug: Set<number> = new Set();
  target: Set<number> = new Set();
  commercial_status: Set<number> = new Set();
  include_terminated: boolean = false;
  toDTO() {
    return {
      company_id: Array.from(this.company_id),
      therapeutic_area: Array.from(this.therapeutic_area),
      indication: Array.from(this.indication),
      phase: Array.from(this.phase),
      moa: Array.from(this.moa),
      drug_class: Array.from(this.drug_class),
      api: Array.from(this.api),
      drug: Array.from(this.drug),
      target: Array.from(this.target),
      commercial_status: Array.from(this.commercial_status),
      include_terminated: Boolean(this.include_terminated),
    };
  }

  static fromJson(jsonObj) {
    const facetFilter = new FacetFilter();
    facetFilter.company_id = new Set(jsonObj["company_id"]);
    facetFilter.therapeutic_area = new Set(jsonObj["therapeutic_area"]);
    facetFilter.indication = new Set(jsonObj["indication"]);
    facetFilter.phase = new Set(jsonObj["phase"]);
    facetFilter.moa = new Set(jsonObj["moa"]);
    facetFilter.drug_class = new Set(jsonObj["drug_class"]);
    facetFilter.api = new Set(jsonObj["api"]);
    facetFilter.drug = new Set(jsonObj["drug"]);
    facetFilter.target = new Set(jsonObj["target"]);
    facetFilter.commercial_status = new Set(jsonObj["commercial_status"]);
    facetFilter.include_terminated = Boolean(jsonObj["include_terminated"]);
    return facetFilter;
  }
}

export const GridColumnNames = {
  drug_name: "Drug Name",
  ticker: "Ticker",
  companyname: "Company Name",
  indications: "Indications by Phase",
  moa: "Mechanism of Action (MOA)",
  target: "Target",
  lines: "Lines",
  commercial_status: "Drug Commercial Status",
  therapeutic_area: "Therapeutic Area",
  probability_of_success: "POS",
  probability_of_success_display: "Probability of Success (POS)",
  launch_year: "Launch Year",
  approval_date: "Approval Date",
  peak_value: "Peak Sales",
  peak_year: "Peak Sales Year",
  max_value: "Max Sales",
  max_year: "Max Sales Year",
  drug_type: "Drug Type",
  drug_class: "Drug Class",
  api: "API",
  delivery_mechanism: "Delivery Mechanism",
  revenue_type: "Revenue Type",
  originator_licensee: "Originator/Licensee",
  generic_biosimilar: "Generics/Biosimilars",
  alias_name: "Alias Name",
  expiry_date: "Expiry Date",
  dapparameter: "Excel Code",
  aggregate_year_5: "5 Years Aggregate",
};

export const SearchCategories = {
  companyname: "Company",
  ticker: "Company",
  phase: "Clinical Phases (by Indication)",
  indication: "Indication",
  moa: "MOA",
  drug: "Drug Name",
  therapeutic_area: "Therapeutic Area",
  drug_class: "Drug Class",
  target: "Target",
  api: "API",
  alias: "Drug Name",
  commercial_status: "Drug Commercial Status",
};

export interface GridRowObj {
  ctag: number;
  dataObj: any;
  children: GridRowObj[];
  parentCtag?: number;
}

export const BreakOutColsAllowed = [
  "drug_name",
  "indications",
  "dapparameter",
  "revenue_type",
];
